import { useState } from 'react';
import { Image as ImageIcon, Download, Sparkles, RefreshCw } from 'lucide-react';
import { downloadBlob } from '../lib/utils';
import { IMAGE_MODELS } from '../lib/models';
import PageHeader from '../components/ui/PageHeader';
import GlassCard from '../components/ui/GlassCard';
import Button from '../components/ui/Button';
import PromptInput from '../components/generation/PromptInput';
import ModelSelector from '../components/generation/ModelSelector';
import AspectRatioPicker from '../components/generation/AspectRatioPicker';
import GenerationProgress from '../components/generation/GenerationProgress';
import ResultReveal from '../components/generation/ResultReveal';
import { useGenerate } from '../hooks/useGenerate';

const SAMPLES = [
  'A bioluminescent forest at night, volumetric light, cinematic',
  'Retro-futuristic city skyline, synthwave palette, ultra detailed',
  'A cozy reading nook by a rainy window, warm lighting, photoreal',
];

export default function ImageStudio() {
  const [prompt, setPrompt] = useState('');
  const [model, setModel] = useState(IMAGE_MODELS[0].value);
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const { generate, loading, elapsed, result } = useGenerate('/generate/image');

  const run = () => prompt.trim() && generate({ prompt, model, aspectRatio });

  const download = async () => {
    const res = await fetch(result.outputUrl);
    downloadBlob(await res.blob(), `aether-${result.id}.png`);
  };

  return (
    <div>
      <PageHeader
        title="Image studio"
        subtitle="Turn a prompt into art with open diffusion models."
        icon={ImageIcon}
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,420px)_1fr]">
        {/* Controls */}
        <div className="space-y-4">
          <PromptInput
            value={prompt}
            onChange={setPrompt}
            onSubmit={run}
            loading={loading}
            placeholder="A neon aurora over a calm ocean, cinematic…"
            actionLabel="Generate"
          />
          <GlassCard className="space-y-4 p-4">
            <ModelSelector value={model} onChange={setModel} models={IMAGE_MODELS} />
            <AspectRatioPicker value={aspectRatio} onChange={setAspectRatio} />
          </GlassCard>

          <div className="flex flex-wrap gap-2">
            {SAMPLES.map((s) => (
              <button
                key={s}
                onClick={() => setPrompt(s)}
                className="rounded-full border border-border/70 px-3 py-1.5 text-xs text-content-muted transition-colors hover:border-brand-400 hover:text-content"
              >
                {s.slice(0, 28)}…
              </button>
            ))}
          </div>
        </div>

        {/* Result */}
        <GlassCard spotlight className="flex min-h-[420px] items-center justify-center overflow-hidden p-4">
          {loading ? (
            <GenerationProgress elapsed={elapsed} label="Rendering image" />
          ) : result ? (
            <ResultReveal className="w-full">
              <img
                src={result.outputUrl}
                alt={result.prompt}
                className="mx-auto max-h-[60vh] w-auto rounded-xl shadow-soft"
              />
              <div className="mt-4 flex items-center justify-between">
                <p className="line-clamp-1 max-w-[60%] text-sm text-content-muted">{result.prompt}</p>
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" icon={RefreshCw} onClick={run}>
                    Again
                  </Button>
                  <Button size="sm" icon={Download} onClick={download}>
                    Download
                  </Button>
                </div>
              </div>
            </ResultReveal>
          ) : (
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="rounded-2xl bg-brand-500/10 p-4 glow-pulse">
                <Sparkles className="h-8 w-8 text-brand-300" />
              </div>
              <p className="font-medium">Your image will appear here</p>
              <p className="max-w-xs text-sm text-content-muted">
                Write a prompt, pick a model and ratio, then hit Generate.
              </p>
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}
